import { useLayoutEffect, useState } from 'react';

/**
 * S334: "tam sayfa" tablo ekranı için sayfa kökünün yüksekliğini SABİT FORMÜLLE DEĞİL, kaydırma
 * kabındaki (`main`, bkz. `Layout.tsx`) gerçek konumunu ÖLÇEREK verir. Yerlice
 * `lib/doluYukseklik.ts` mantığının Template karşılığı.
 *
 * NEDEN `calc(100vh - header - dolgu)` YETMEZ: kökün üstündeki yükseklik sabit değil. Filtre barı
 * yuvası (`FiltreBarYuvasi`) telefonda sarıp iki üç satıra çıkar; demo şeridi, deneme şeridi ve veri
 * tazeliği şeridi firmaya göre var ya da yok. Formül her birinde ayrı bir sihirli sayı isterdi ve
 * yapışık toplam satırı görünürün altına inerdi. Muhakeme etmiyoruz, ölçüyoruz.
 *
 * `kip = 'enAz'` (telefon, kart kipi): yükseklik KİLİTLENMEZ, `min-height` yazılır. Kart listesinin kendi
 * kaydırıcısı yoktur; kilit kalsaydı liste kabın dışına taşıp kırpılırdı (Yerlice `mobil.css` S127:
 * 50 kartın 7'si erişilebiliyordu).
 *
 * Dönen değer CALLBACK REF (`useRef` değil): kök koşullu çizilirse (iskelet -> tablo) effect geç
 * bağlanan düğümde de koşar.
 *
 * jsdom yerleşim ölçmez: hesaplanan sayı testte doğrulanamaz. Test kancanın bağlı olduğunu ve
 * `main` yokken dokunmadığını korur; sayı tarayıcıda ölçülür.
 */
export function useDoluYukseklik<T extends HTMLElement>(kip: 'yukseklik' | 'enAz' = 'yukseklik') {
  const [el, setEl] = useState<T | null>(null);

  useLayoutEffect(() => {
    if (!el) return;
    const kap = el.closest('main');
    // Kap yoksa (izole render / test) dokunma.
    if (!kap) return;

    const olc = () => {
      // 1) Kendi yüksekliğini denklemden çıkar: kap taşmışken okunan `top` yalan söyler.
      el.style.height = '0px';

      // 2) Kap içindeki üst boşluk = üstte ne varsa (filtre barı yuvası, sayfa dolgusu).
      const ust = el.getBoundingClientRect().top - kap.getBoundingClientRect().top + kap.scrollTop;

      // 3) Alt boşluk: el ile kap ARASINDAKİ ataların alt dolgusu (sayfa dolgusu değişirse izler).
      let alt = 0;
      for (let a = el.parentElement; a && a !== kap; a = a.parentElement) {
        alt += parseFloat(getComputedStyle(a).paddingBottom) || 0;
      }
      alt += parseFloat(getComputedStyle(kap).paddingBottom) || 0;

      if (kip === 'enAz') {
        // Kart kipi: kök kilitlenmez, yalnız EN AZ ekranın dibine kadar uzar (toplam şeridi dipte dursun);
        // liste uzunsa sayfa kayar. Alt dolgu düşülmez: kök onu eksi kenar boşluğuyla kapatır.
        el.style.height = '';
        el.style.minHeight = `${Math.max(0, kap.clientHeight - ust)}px`;
      } else {
        el.style.minHeight = '';
        el.style.height = `${Math.max(0, kap.clientHeight - ust - alt)}px`;
      }
    };

    olc();

    // Yeniden ölç: pencere boyutu ve üstteki barın yüksekliği değişebilir (filtre barı sarar).
    // el'in KENDİSİ gözlenmez (yüksekliğini biz değiştiriyoruz: sonsuz döngü olurdu).
    const gozlenen: Element[] = [kap];
    for (let a: Element | null = el; a && a !== kap; a = a.parentElement) {
      for (let k = a.previousElementSibling; k; k = k.previousElementSibling) gozlenen.push(k);
    }
    const ro = new ResizeObserver(olc);
    for (const g of gozlenen) ro.observe(g);
    window.addEventListener('resize', olc);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', olc);
      el.style.height = '';
      el.style.minHeight = '';
    };
  }, [el, kip]);

  return setEl;
}
